/**
 * Session state for the whole app.
 *
 * Responsibilities kept here, not scattered: who is signed in, what features they have, how
 * much credit they have left, and what happens when a session dies mid-action.
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { refreshSession, setSessionExpiredHandler } from '@/shared/api/client';
import { authApi } from '@/shared/api/endpoints';
import { isApiError } from '@/shared/api/errors';
import {
  broadcastSession,
  clearTokens,
  getAccessToken,
  hasSessionHint,
  onSessionEvent,
  setTokens,
} from '@/shared/api/tokens';
import type { CreditSummary, User } from '@/shared/api/types';

export interface AuthContextValue {
  user: User | null;
  /** True until the first /auth/me settles, so guards do not flash the login screen. */
  isLoading: boolean;
  isAuthenticated: boolean;
  mustChangePassword: boolean;
  credits: CreditSummary | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
  hasFeature: (key: string) => boolean;
  featureName: (key: string) => string;
  isAdmin: boolean;
  /** Set while the in-place re-auth dialog is open. */
  reauthOpen: boolean;
  resolveReauth: (success: boolean) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * How long to keep trying to load the user when the failure is not the session's fault — the
 * network dropped, or the server was mid-deploy. Without this a two-second outage at page load
 * showed the login screen to someone whose session was perfectly good.
 */
const LOAD_RETRY_DELAYS_MS = [800, 2500];

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/** A failure that says nothing about whether the user is signed in. */
const isTransient = (error: unknown) =>
  isApiError(error) && (error.status === 0 || error.status >= 500 || error.status === 429);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  // Loading until we know: there is a token to check, or a session cookie to resume from.
  const [isLoading, setIsLoading] = useState(() => Boolean(getAccessToken()) || hasSessionHint());
  const [reauthOpen, setReauthOpen] = useState(false);

  // Resolver for the promise the API client is awaiting while the dialog is open.
  const reauthResolver = useRef<((success: boolean) => void) | null>(null);

  /**
   * Load who is signed in, resuming the session first if the access token is gone.
   *
   * The access token lives in localStorage, which a browser may clear or purge (Safari does after
   * a week away). The session itself does not: it is an HttpOnly cookie. So a visitor with no
   * token but with the session marker gets a refresh before being called signed out.
   *
   * `retryDelays` is for the initial load only, where being wrong is a login screen for someone
   * who is signed in. Everything else calls it with none and fails fast.
   */
  const loadUser = useCallback(async (retryDelays: number[]): Promise<User | null> => {
    try {
      for (let attempt = 0; ; attempt += 1) {
        // Whether to try again, and after how long — for a failure that is not the session's fault.
        const retryOrGiveUp = async (): Promise<boolean> => {
          if (attempt >= retryDelays.length) return false;
          await sleep(retryDelays[attempt]);
          return true;
        };

        if (!getAccessToken()) {
          if (!hasSessionHint()) {
            setUser(null);
            return null;
          }
          const outcome = await refreshSession();
          if (outcome.status === 'rejected') {
            // The session is over, and the server has said so to the cookies too.
            clearTokens();
            setUser(null);
            return null;
          }
          if (outcome.status === 'unavailable') {
            // We could not ask. Nothing is cleared, so a retry — or a reload — picks it back up.
            if (await retryOrGiveUp()) continue;
            setUser(null);
            return null;
          }
        }

        try {
          const me = await authApi.me();
          setUser(me);
          return me;
        } catch (error) {
          // A 401 here means the token and the session are both gone; anything else (a network
          // blip, a server restarting) should not sign the user out.
          if (isApiError(error) && error.isAuthError) {
            clearTokens();
            setUser(null);
            return null;
          }
          if (isTransient(error) && (await retryOrGiveUp())) continue;
          return null;
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(() => loadUser([]), [loadUser]);

  useEffect(() => {
    void loadUser(LOAD_RETRY_DELAYS_MS);
  }, [loadUser]);

  const logout = useCallback(() => {
    // The session cookie is what keeps someone signed in across restarts, so it has to be told to
    // go. Best effort and not awaited: signing out here should not wait on the network, and the
    // marker below is cleared regardless, which is what the app itself looks at.
    void authApi.logout().catch(() => undefined);
    clearTokens();
    setUser(null);
    queryClient.clear();
    broadcastSession({ type: 'logout' });
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string): Promise<User> => {
      const result = await authApi.login(email, password);
      // The refresh token in the response is ignored on purpose: the server has already put it
      // in an HttpOnly cookie, which is the only copy the browser keeps.
      setTokens(result.access_token);
      setUser(result.user);
      queryClient.clear();
      broadcastSession({ type: 'login' });
      return result.user;
    },
    [queryClient],
  );

  /** Called by the re-auth dialog once the user has signed in again (or given up). */
  const resolveReauth = useCallback((success: boolean) => {
    setReauthOpen(false);
    reauthResolver.current?.(success);
    reauthResolver.current = null;
  }, []);

  // Teach the API client to ask us instead of navigating away. This is what lets an editor
  // session survive an expired token: the request pauses, the user signs in, it replays.
  useEffect(() => {
    setSessionExpiredHandler(async () => {
      setUser(null);
      setReauthOpen(true);
      return new Promise<boolean>((resolve) => {
        reauthResolver.current = resolve;
      });
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  // Signing out in one tab signs out the others.
  useEffect(
    () =>
      onSessionEvent((event) => {
        if (event.type === 'logout') {
          setUser(null);
          queryClient.clear();
        } else {
          void refreshUser();
        }
      }),
    [queryClient, refreshUser],
  );

  const hasFeature = useCallback(
    (key: string) => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      return user.features.some((feature) => feature.key === key && feature.enabled);
    },
    [user],
  );

  const featureName = useCallback(
    (key: string) => user?.features.find((feature) => feature.key === key)?.name ?? key,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: Boolean(user),
      mustChangePassword: Boolean(user?.must_change_pwd),
      credits: user?.credits ?? null,
      login,
      logout,
      refreshUser,
      hasFeature,
      featureName,
      isAdmin: user?.role === 'admin',
      reauthOpen,
      resolveReauth,
    }),
    [user, isLoading, login, logout, refreshUser, hasFeature, featureName, reauthOpen, resolveReauth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
